import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Card, Button, Input, Select, Label, EmptyState } from "../components/ui-kit";
import { SearchSelect } from "../components/SearchSelect";
import { Plus, Target as TargetIcon, FileDown, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { thisMonthIST } from "../lib/datefmt";

const thisMonth = () => thisMonthIST();
const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function TargetsPage() {
  const { user } = useAuth();
  const canManage = ["admin", "manager", "director", "sales_manager"].includes(user?.role);
  const [period, setPeriod] = useState(thisMonth());
  const [targetUser, setTargetUser] = useState("");
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [ach, setAch] = useState(null);
  const [openOT, setOpenOT] = useState(false);
  const [openCT, setOpenCT] = useState(false);

  const load = async () => {
    const [usersR, cR, pR] = await Promise.all([
      canManage ? api.get("/users") : Promise.resolve({ data: [] }),
      api.get("/customers"),
      api.get("/products"),
    ]);
    setUsers(usersR.data.filter((u) => ["sales", "sales_manager", "rm_and_above"].includes(u.role)));
    setCustomers(cR.data);
    setProducts(pR.data);
  };
  useEffect(() => {
    load();
    // Initial fetch only; `canManage` is derived from auth context and stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAch = async () => {
    const params = { period };
    if (canManage && targetUser) params.user_id = targetUser;
    const { data } = await api.get("/targets/achievement", { params });
    setAch(data);
  };
  useEffect(() => {
    loadAch();
    // Re-fetch achievement when the period or selected user changes; `loadAch`
    // closure intentionally captures the latest values via state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, targetUser]);

  return (
    <div>
      <PageHeader
        title="Targets & Achievement"
        subtitle="Feed targets per customer & product, track achievement vs plan."
        actions={
          <>
            <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-40" data-testid="targets-period" />
            {canManage && (
              <Select value={targetUser} onChange={(e) => setTargetUser(e.target.value)} className="w-52">
                <option value="">Select sales person…</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </Select>
            )}
            {canManage && (
              <>
                <Button variant="outline" onClick={() => setOpenOT(true)}>
                  <Plus className="w-4 h-4" /> Order target
                </Button>
                <Button variant="outline" onClick={() => setOpenCT(true)}>
                  <Plus className="w-4 h-4" /> Collection target
                </Button>
              </>
            )}
          </>
        }
      />

      {openOT && (
        <OrderTargetForm
          users={users}
          customers={customers}
          products={products}
          period={period}
          defaultUser={targetUser}
          onClose={() => setOpenOT(false)}
          onSaved={() => {
            setOpenOT(false);
            loadAch();
          }}
        />
      )}
      {openCT && (
        <CollectionTargetForm
          users={users}
          customers={customers}
          period={period}
          defaultUser={targetUser}
          onClose={() => setOpenCT(false)}
          onSaved={() => {
            setOpenCT(false);
            loadAch();
          }}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {canManage && (
          <BulkTargetsCard
            users={users}
            period={period}
            defaultUser={targetUser}
            onDone={loadAch}
          />
        )}
        {canManage && (
          <BulkTargetsCard
            users={users}
            period={period}
            defaultUser={targetUser}
            kind="collections"
            onDone={loadAch}
          />
        )}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <TargetIcon className="w-4 h-4 text-[#1E3F20]" />
            <h3 className="font-heading text-xl font-bold">Order targets</h3>
          </div>
          {!ach || ach.order_targets.length === 0 ? (
            <EmptyState title="No order targets for this period" />
          ) : (
            <div className="space-y-3">
              {ach.order_targets.map((t) => {
                const pct = t.target_qty ? Math.min(100, Math.round((t.achieved_qty * 100) / t.target_qty)) : 0;
                const cust = customers.find((c) => c.id === t.customer_id);
                const prod = products.find((p) => p.id === t.product_id);
                return (
                  <div key={t.id} className="border border-[#E5E2DC] rounded-lg p-3">
                    <div className="flex justify-between text-sm">
                      <div>
                        <div className="font-semibold">{cust?.name || t.customer_id}</div>
                        <div className="text-xs text-[#5C6B5E]">{prod?.name || t.product_id}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">
                          {t.achieved_qty} / {t.target_qty}
                        </div>
                        <div className="text-xs text-[#5C6B5E]">{pct}%</div>
                      </div>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-[#F2EDE4] overflow-hidden">
                      <div className="h-full bg-[#1E3F20]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <TargetIcon className="w-4 h-4 text-[#1E3F20]" />
            <h3 className="font-heading text-xl font-bold">Collection targets</h3>
          </div>
          {!ach || ach.collection_targets.length === 0 ? (
            <EmptyState title="No collection targets for this period" />
          ) : (
            <div className="space-y-3">
              {ach.collection_targets.map((t) => {
                const pct = t.target_amount ? Math.min(100, Math.round((t.achieved_amount * 100) / t.target_amount)) : 0;
                const cust = customers.find((c) => c.id === t.customer_id);
                return (
                  <div key={t.id} className="border border-[#E5E2DC] rounded-lg p-3">
                    <div className="flex justify-between text-sm">
                      <div>
                        <div className="font-semibold">{cust?.name || t.customer_id}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">
                          {money(t.achieved_amount)} / {money(t.target_amount)}
                        </div>
                        <div className="text-xs text-[#5C6B5E]">{pct}%</div>
                      </div>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-[#F2EDE4] overflow-hidden">
                      <div className="h-full bg-[#E07A5F]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function OrderTargetForm({ users, customers, products, period, defaultUser, onClose, onSaved }) {
  const [form, setForm] = useState({
    user_id: defaultUser || (users[0]?.id ?? ""),
    customer_id: "",
    product_id: "",
    target_qty: "",
    target_value: "",
    period,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/targets/orders", {
        ...form,
        target_qty: Number(form.target_qty),
        target_value: Number(form.target_value || 0),
      });
      toast.success("Target saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  return (
    <Card className="mb-6">
      <h3 className="font-heading text-xl font-bold mb-4">New order target</h3>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Sales person</Label>
          <Select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} required>
            <option value="">Select…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Customer</Label>
          <Select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} required>
            <option value="">Select…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.code})
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Product</Label>
          <Select value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} required>
            <option value="">Select…</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.packing_size}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Period</Label>
          <Input type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
        </div>
        <div>
          <Label>Target Qty</Label>
          <Input type="number" step="0.01" value={form.target_qty} onChange={(e) => setForm({ ...form, target_qty: e.target.value })} required />
        </div>
        <div>
          <Label>Target Value (₹)</Label>
          <Input type="number" step="0.01" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} />
        </div>
        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Save target</Button>
        </div>
      </form>
    </Card>
  );
}

function CollectionTargetForm({ users, customers, period, defaultUser, onClose, onSaved }) {
  const [form, setForm] = useState({
    user_id: defaultUser || (users[0]?.id ?? ""),
    customer_id: "",
    target_amount: "",
    period,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/targets/collections", { ...form, target_amount: Number(form.target_amount) });
      toast.success("Target saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  return (
    <Card className="mb-6">
      <h3 className="font-heading text-xl font-bold mb-4">New collection target</h3>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Sales person</Label>
          <Select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} required>
            <option value="">Select…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Customer</Label>
          <Select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} required>
            <option value="">Select…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.code})
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Period</Label>
          <Input type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
        </div>
        <div>
          <Label>Target Amount (₹)</Label>
          <Input type="number" step="0.01" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} required />
        </div>
        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Save target</Button>
        </div>
      </form>
    </Card>
  );
}


function BulkTargetsCard({ users, period, defaultUser, onDone, kind = "orders" }) {
  const fileRef = useRef(null);
  const [pickedUser, setPickedUser] = useState(defaultUser || "");
  const [busy, setBusy] = useState(false);
  const isCol = kind === "collections";
  const dlUrl = isCol ? "/targets/collections/template.xlsx" : "/targets/orders/template.xlsx";
  const upUrl = isCol ? "/targets/collections/upload" : "/targets/orders/upload";
  const title = isCol ? "Bulk collection targets" : "Bulk order targets — product × dealer";
  const description = isCol
    ? "Excel of dealers with target ₹ amount per dealer for the period. Re-upload to replace."
    : "Excel with products in rows and dealers in columns. Fill qty; ₹ value auto-computes from the user's price list.";
  const testPrefix = isCol ? "bulk-collection-targets" : "bulk-targets";

  useEffect(() => {
    if (defaultUser) setPickedUser(defaultUser);
  }, [defaultUser]);

  const download = async () => {
    if (!pickedUser) return toast.error("Select a sales person first");
    try {
      const res = await api.get(dlUrl, {
        params: { user_id: pickedUser, period },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      const u = users.find((x) => x.id === pickedUser);
      a.href = url;
      a.download = `${isCol ? "collection_" : ""}targets_${(u?.name || "user").replace(/\s+/g, "_")}_${period}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!pickedUser) return toast.error("Select a sales person first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`${upUrl}?user_id=${pickedUser}&period=${period}`, fd);
      const { created, skipped, errors } = res.data;
      toast.success(`Uploaded — ${created} targets saved${skipped ? `, ${skipped} skipped` : ""}`);
      if (errors && errors.length) {
        toast.warning(`${errors.length} row issue(s): ${errors.slice(0, 2).join(" | ")}`);
      }
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Card className="border-2 border-[#DDA15E]/30 bg-gradient-to-br from-[#DDA15E]/5 to-white" data-testid={`${testPrefix}-card`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[#DDA15E] text-white grid place-items-center">
          <Upload className="w-5 h-5" strokeWidth={2.25} />
        </div>
        <div>
          <h3 className="font-heading text-xl font-bold">{title}</h3>
          <p className="text-xs text-[#5C6B5E] mt-1">{description}</p>
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <Label>Sales person</Label>
          <SearchSelect
            value={pickedUser}
            onChange={setPickedUser}
            options={users.map((u) => ({
              value: u.id,
              label: u.name,
              sublabel: u.email,
              keywords: [u.role, u.team_code].filter(Boolean),
            }))}
            placeholder="Pick a sales person…"
            testId={`${testPrefix}-user`}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={download} disabled={!pickedUser} data-testid={`${testPrefix}-download`}>
            <FileDown className="w-4 h-4" /> Download template ({period})
          </Button>
          <label
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 font-semibold text-sm cursor-pointer ${
              busy || !pickedUser ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-[#1E3F20] text-white hover:bg-[#2C5530]"
            }`}
          >
            <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload filled sheet"}
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              disabled={busy || !pickedUser}
              onChange={(e) => upload(e.target.files?.[0])}
              data-testid={`${testPrefix}-upload`}
            />
          </label>
        </div>
        <p className="text-[11px] text-[#8A8577]">
          Upload replaces this user's {isCol ? "collection" : "order"} targets for {period}. Only dealers currently assigned to this user are accepted.
        </p>
      </div>
    </Card>
  );
}
